import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar, Footer } from '../components/Navbar';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Plus, User2, Image as ImageIcon, TreeDeciduous, QrCode, Mail, Lock, Sparkles, Settings as SettingsIcon, ShieldCheck, Package, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import CompleteProfileGate from '../components/CompleteProfileGate';

export default function Dashboard() {
  const { user } = useAuth();
  const [owned, setOwned] = useState([]);
  const [shared, setShared] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  // iter67 — surface the "profile not yet completed" state so the user
  // can re-open the gate themselves whenever they're ready.
  const [profileCompleted, setProfileCompleted] = useState(true);
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    api.get('/memorials').then(({ data }) => {
      setOwned(data.owned || []);
      setShared(data.shared || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Poll the canonical profile flag so the reminder banner appears
  // exactly when the gate is also showing it. Re-runs every time the
  // dashboard re-mounts (e.g. after the user dismisses + comes back).
  useEffect(() => {
    if (!user) return;
    let alive = true;
    api.get('/users/me/profile').then(({ data }) => {
      if (!alive) return;
      setProfileCompleted(Boolean(data?.profile_completed));
    }).catch(() => {});
    return () => { alive = false; };
  }, [user]);

  // Partners belong on their partner dashboard, not the family list.
  // iter82 — covers both new account types + legacy `professional`.
  useEffect(() => {
    if (!user) return;
    if (user.account_type === 'regional_partner') {
      nav('/partners/regional', { replace: true });
    } else if (user.account_type === 'memorial_partner' || user.account_type === 'professional') {
      nav('/partners/memorial', { replace: true });
    }
  }, [user, nav]);

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const cancelSelect = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    const names = owned.filter((m) => selected.has(m.memorial_id)).map((m) => m.full_name).join(', ');
    const ok = window.confirm(
      `Delete ${selected.size} memorial${selected.size > 1 ? 's' : ''} (${names})?\n\n` +
      `This is permanent — photos, videos, family tree, and all memories will be removed.`
    );
    if (!ok) return;
    const typed = window.prompt('To confirm, type DELETE in capitals:');
    if (typed !== 'DELETE') {
      toast.error('Deletion cancelled');
      return;
    }
    setDeleting(true);
    const ids = Array.from(selected);
    const results = await Promise.allSettled(
      ids.map((id) => api.delete(`/memorials/${id}`))
    );
    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length === 0) {
      toast.success(`Deleted ${ids.length} memorial${ids.length > 1 ? 's' : ''}`);
    } else {
      toast.error(`${failed.length} of ${ids.length} could not be deleted`);
    }
    cancelSelect();
    setDeleting(false);
    load();
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <CompleteProfileGate />
      <Navbar />

      <main className="flex-1 ml-container py-12">
        {(user?.role === 'admin' || user?.role === 'platform_admin') && (
          <div
            data-testid="dashboard-admin-banner"
            className="mb-8 rounded-xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--primary))] text-[hsl(var(--accent))] px-5 py-4 sm:px-6 sm:py-5 shadow-[0_18px_40px_-25px_rgba(26,54,38,0.45)]"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#D9B775] mb-1">
                  Platform admin
                </div>
                <div className="font-serif text-2xl text-[#F4E5C2]">
                  Admin console
                </div>
                <p className="text-[13px] text-[#F4E5C2]/85 mt-1.5">
                  Manage partner applications, override contracts, promo codes, assistance requests and more.
                </p>
              </div>
              <Link
                to="/admin/reports"
                data-testid="dashboard-admin-open-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-[12px] tracking-[0.2em] uppercase font-semibold bg-[#D9B775] text-[#1A3626] hover:bg-[#F4E5C2] transition-colors shrink-0"
              >
                Open admin console →
              </Link>
            </div>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-7 gap-2">
              {[
                { to: '/admin/reports', label: 'Reports', testid: 'dashboard-admin-quick-reports' },
                { to: '/admin/families', label: 'Family Accounts', testid: 'dashboard-admin-quick-families' },
                { to: '/admin/partners/applications', label: 'Partner Applications', testid: 'dashboard-admin-quick-partner-applications' },
                { to: '/admin/partners/overrides', label: 'Override Contracts', testid: 'dashboard-admin-quick-override-contracts' },
                { to: '/admin/promo-codes', label: 'Promo Codes', testid: 'dashboard-admin-quick-promo-codes' },
                { to: '/admin/assistance', label: 'Assistance Requests', testid: 'dashboard-admin-quick-assistance-requests' },
                { to: '/admin/professional/applications', label: 'Professional (legacy)', testid: 'dashboard-admin-quick-professional' },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  className="block text-center px-2 py-2 rounded-md text-[12px] font-medium bg-[#F4E5C2]/10 text-[#F4E5C2] hover:bg-[#F4E5C2]/20 transition-colors"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="ml-overline" data-testid="dashboard-user-label">
              {user?.name ? user.name : 'Your memorial space'}
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl text-[hsl(var(--primary))] mt-2">
              Welcome, {(user?.first_name
                || user?.name?.split(' ')[0]
                || user?.email?.split('@')[0]
                || 'there')}.
            </h1>
            <p className="text-[hsl(var(--muted-foreground))] text-sm mt-2">
              Plan: <span className="font-medium text-[hsl(var(--primary))] capitalize">{user?.plan}</span> ·
              Signed in as <span className="font-medium" data-testid="dashboard-user-email">{user?.email}</span>
            </p>
            {/* iter67 — Profile completion reminder. Renders only when the
                user has dismissed the gate without filling it out. Clicking
                the link re-opens the gate (CompleteProfileGate listens to
                the ?complete_profile=1 query param). */}
            {user && !profileCompleted && (
              <div
                className="mt-4 inline-flex items-center gap-2 rounded-md border border-[hsl(var(--accent))]/45 bg-[hsl(var(--accent)/0.08)] px-4 py-2.5 text-[12px] tracking-[0.05em] text-[hsl(var(--primary))]"
                data-testid="dashboard-profile-reminder"
              >
                <span className="text-[hsl(var(--accent))]">●</span>
                Your admin profile is incomplete.
                <Link
                  to="/dashboard?complete_profile=1"
                  className="font-semibold underline decoration-[hsl(var(--accent))] underline-offset-2 hover:text-[hsl(var(--accent))]"
                  data-testid="dashboard-profile-reminder-link"
                >
                  Complete profile →
                </Link>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {owned.length > 0 && !selectMode && (
              <button
                onClick={() => setSelectMode(true)}
                className="ml-btn-outline"
                data-testid="dashboard-select-mode-btn"
              >
                <Trash2 className="w-3.5 h-3.5" /> Manage
              </button>
            )}
            {selectMode && (
              <>
                <span className="text-xs tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.6)]" data-testid="dashboard-selected-count">
                  {selected.size} selected
                </span>
                <button
                  onClick={deleteSelected}
                  disabled={selected.size === 0 || deleting}
                  className="ml-btn-outline border-[hsl(var(--destructive))]/50 text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/5"
                  data-testid="dashboard-delete-selected-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" /> {deleting ? 'Deleting…' : 'Delete selected'}
                </button>
                <button
                  onClick={cancelSelect}
                  className="ml-btn-ghost"
                  data-testid="dashboard-select-cancel-btn"
                >
                  Cancel
                </button>
              </>
            )}
            <Link to="/dashboard/new" className="ml-btn-gold" data-testid="new-memorial-btn">
              <Plus className="w-4 h-4" /> Begin a memorial
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="mt-16 font-serif text-xl text-[hsl(var(--muted-foreground))]">Loading…</div>
        ) : owned.length === 0 && shared.length === 0 ? (
          <div className="mt-14 ml-card text-center !p-16">
            <Logo56 />
            <h2 className="font-serif text-3xl mt-6 text-[hsl(var(--primary))]">No memorial yet.</h2>
            <p className="text-[hsl(var(--muted-foreground))] text-sm mt-3 max-w-md mx-auto">
              When you're ready, begin a gentle 6-step journey to create a lasting tribute.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/dashboard/new" className="ml-btn-primary" data-testid="empty-new-memorial-btn">
                <Plus className="w-4 h-4" /> Create a memorial
              </Link>
              <Link to="/m/michael-james-anderson" className="ml-btn-outline">
                View demo memorial
              </Link>
            </div>
          </div>
        ) : (
          <>
            {owned.length > 0 && (
              <section className="mt-12">
                <div className="ml-overline">Memorials you own</div>
                <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {owned.map((m) => (
                    <MemorialCard
                      key={m.memorial_id}
                      m={m}
                      isOwner
                      selectMode={selectMode}
                      selected={selected.has(m.memorial_id)}
                      onToggle={() => toggleSelected(m.memorial_id)}
                    />
                  ))}
                </div>
              </section>
            )}
            {shared.length > 0 && (
              <section className="mt-14">
                <div className="ml-overline">Shared with you as admin</div>
                <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {shared.map((m) => <MemorialCard key={m.memorial_id} m={m} />)}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}

function Logo56() {
  return (
    <svg width="56" height="56" viewBox="0 0 64 64" className="mx-auto">
      <circle cx="32" cy="32" r="29" fill="none" stroke="#1A3626" strokeWidth="1.6" />
      <path d="M32 50 L32 34" stroke="#1A3626" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M32 18 C22 18 17 27 21 33 C15 34 14 42 22 42 C22 38 28 34 32 34 C36 34 42 38 42 42 C50 42 49 34 43 33 C47 27 42 18 32 18 Z" fill="#6B705C" />
      <path d="M48 52 C52 50 55 46 54 42 C50 44 47 47 48 52 Z" fill="#C5A059" />
    </svg>
  );
}

function MemorialCard({ m, isOwner, selectMode, selected, onToggle }) {
  // Owners get a slightly darker, gold-edged card so theirs are
  // immediately distinguishable from memorials shared with them.
  const ownerSurface = isOwner
    ? 'bg-[hsl(var(--primary)/0.04)] border-[hsl(var(--accent))]/50 shadow-[0_1px_0_rgba(197,160,89,0.18)_inset]'
    : '';
  const cardClass = `ml-card transition block relative ${ownerSurface} ${
    selectMode
      ? selected
        ? 'border-[hsl(var(--destructive))]/50 bg-[hsl(var(--destructive))]/5 cursor-pointer'
        : 'hover:border-[hsl(var(--primary))] cursor-pointer'
      : isOwner
      ? 'hover:border-[hsl(var(--accent))] hover:shadow-md group'
      : 'hover:border-[hsl(var(--accent))] group'
  }`;

  const body = (
    <>
      {selectMode && isOwner && (
        <div className="absolute top-4 right-4" data-testid={`memorial-card-select-${m.memorial_id}`}>
          <input
            type="checkbox"
            className="w-5 h-5 accent-[hsl(var(--destructive))] cursor-pointer"
            checked={!!selected}
            onChange={(e) => { e.stopPropagation(); onToggle?.(); }}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select ${m.full_name}`}
          />
        </div>
      )}
      {isOwner && (
        <div
          className="absolute top-3 left-3 text-[9px] tracking-[0.22em] uppercase font-semibold px-2 py-1 rounded-full bg-[hsl(var(--accent))] text-white shadow-sm"
          data-testid={`memorial-card-owned-tag-${m.memorial_id}`}
        >
          Owned
        </div>
      )}
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full overflow-hidden border border-[hsl(var(--accent))]/40 bg-[hsl(var(--muted))]">
          {m.profile_photo_url ? (
            <img src={m.profile_photo_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[hsl(var(--primary))] font-serif text-xl">
              {m.full_name?.[0] || 'M'}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-serif text-xl text-[hsl(var(--primary))] truncate">{m.full_name}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
            {m.date_of_birth || '—'} — {m.date_of_passing || '—'}
          </div>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-3 text-xs">
        <span className="flex items-center gap-1.5 text-[hsl(var(--muted-foreground))]"><QrCode className="w-3.5 h-3.5" /> /m/{m.slug}</span>
        <span className="flex items-center gap-1.5 text-[hsl(var(--muted-foreground))]">Scans: {m.scan_count || 0}</span>
        {isOwner && <span className="ml-overline !text-[hsl(var(--accent))] text-[9px]">Owner</span>}
        {m.plan_status === 'pending' && (
          <span
            className="text-[9px] tracking-[0.22em] uppercase font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300"
            data-testid={`memorial-card-unpaid-${m.memorial_id}`}
          >
            Activation pending
          </span>
        )}
        {m.plan_status === 'paid' && (
          <span
            className="text-[9px] tracking-[0.22em] uppercase font-semibold px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300"
            data-testid={`memorial-card-paid-${m.memorial_id}`}
          >
            {m.plan_id || 'Active'}
          </span>
        )}
      </div>
    </>
  );

  // In select-mode, clicking the card toggles selection (anywhere except the
  // checkbox itself). Otherwise it's a regular link to the memorial dashboard.
  if (selectMode && isOwner) {
    return (
      <div
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle?.(); } }}
        data-testid={`memorial-card-${m.memorial_id}`}
        className={cardClass}
      >
        {body}
      </div>
    );
  }

  return (
    <Link
      to={`/dashboard/m/${m.memorial_id}`}
      data-testid={`memorial-card-${m.memorial_id}`}
      className={cardClass}
    >
      {body}
    </Link>
  );
}

// Memorial detail cards navigation (dashboard cards)
export function MemorialDashboardCards({ memorialId }) {
  const cards = [
    { to: `/dashboard/m/${memorialId}/profile`, icon: User2, title: 'Memorial Profile', desc: 'Name, dates, life summary' },
    { to: `/dashboard/m/${memorialId}/media`, icon: ImageIcon, title: 'Upload Memories', desc: 'Photos, videos, captions' },
    { to: `/dashboard/m/${memorialId}/family`, icon: TreeDeciduous, title: 'Family Tree', desc: 'Olive tree relatives' },
    { to: `/dashboard/m/${memorialId}/qr`, icon: QrCode, title: 'QR Code', desc: 'Download PNG / SVG / PDF' },
    { to: `/dashboard/m/${memorialId}/access`, icon: Mail, title: 'Access Requests', desc: 'Approve or deny vault access' },
    { to: `/dashboard/m/${memorialId}/vault`, icon: Lock, title: 'Family Vault', desc: 'Private photos & family memories' },
    { to: `/dashboard/m/${memorialId}/ai`, icon: Sparkles, title: 'AI Memories', desc: 'Opt-in AI moments' },
    { to: `/dashboard/m/${memorialId}/admins`, icon: ShieldCheck, title: 'Admins', desc: 'Invite family admins' },
    { to: `/dashboard/m/${memorialId}/billing`, icon: Package, title: 'Billing & Package', desc: 'Plan & limits' },
    { to: `/dashboard/m/${memorialId}/settings`, icon: SettingsIcon, title: 'Settings & Audit', desc: 'Audit log, exports' },
  ];
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
      {cards.map((c) => (
        <Link key={c.to} to={c.to} data-testid={`dashboard-card-${c.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              className="ml-card hover:border-[hsl(var(--accent))] group transition block">
          <c.icon className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={1.4} />
          <div className="font-serif text-xl mt-4 text-[hsl(var(--primary))]">{c.title}</div>
          <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{c.desc}</div>
        </Link>
      ))}
    </div>
  );
}
